import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import NewColorRoundButton from '../../components/Button/NewColorRoundButton';
import NormalToggleButton from '../../components/ToggleButton/NormalToggleButton';
import { Body2Regular, Body3Regular, Head3 } from '../../lib/font';
import { colors } from '../../lib/colors';
import { Col, getItemWithExpiration, Row } from '../../lib/utils';
import {
    getCoachPromoProfileAPI,
    updateCoachPromoProfileAPI,
    CoachPromoProfilePayload,
} from '../../api/coachPromo';

type Props = {
    centerId: string;
    coachId: string;
    coachName: string;
    fallbackImageUrl?: string;
    createAlert: any;
    fluid?: boolean;
    onSaved?: () => void;
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 40,
    border: '1px solid #D7E2F2',
    borderRadius: 8,
    padding: '0 12px',
    fontSize: 14,
    color: colors.LAVEL_4,
    outline: 'none',
};

const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    height: 96,
    padding: 12,
    resize: 'none',
    lineHeight: '20px',
};

const TrainerPromoProfileComponent = ({
    centerId,
    coachId,
    coachName,
    fallbackImageUrl,
    createAlert,
    fluid = false,
    onSaved,
}: Props) => {
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [authorization] = useState(getItemWithExpiration('authorization'));

    const [representativeImageUrl, setRepresentativeImageUrl] = useState('');
    const [representativeImageBase64, setRepresentativeImageBase64] = useState('');
    const [headline, setHeadline] = useState('');
    const [tagsText, setTagsText] = useState('');
    const [shortIntro, setShortIntro] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const [sortOrder, setSortOrder] = useState(0);

    const profileQuery = useQuery(
        ['coachPromoProfile', centerId, coachId],
        () => getCoachPromoProfileAPI(centerId, coachId, authorization),
        {
            enabled: !!centerId && !!coachId && !!authorization,
        }
    );

    const profile = profileQuery.data?.data?.profile;
    const displayImageUrl = representativeImageUrl || fallbackImageUrl || '';

    useEffect(() => {
        if (!profile) return;
        setRepresentativeImageUrl(profile.representativeImageUrl || '');
        setRepresentativeImageBase64('');
        setHeadline(profile.headline || '');
        setTagsText((profile.tags || []).join(', '));
        setShortIntro(profile.shortIntro || '');
        setIsVisible(profile.isVisible === true);
        setSortOrder(Number(profile.sortOrder || 0));
    }, [profile]);

    const updateMutation = useMutation({
        mutationFn: (payload: CoachPromoProfilePayload) =>
            updateCoachPromoProfileAPI(centerId, coachId, authorization, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['coachPromoProfile', centerId, coachId] });
            queryClient.invalidateQueries({ queryKey: ['coachPromoProfiles', centerId] });
            queryClient.invalidateQueries({ queryKey: ['getEmployeeInfoAtManageAPIQuery'] });
            setRepresentativeImageBase64('');
            onSaved && onSaved();
            createAlert('홍보 프로필 저장 완료', '태블릿 홍보판 프로필을 저장했습니다.');
        },
        onError: () => {
            createAlert('저장 실패', '홍보 프로필 저장 중 오류가 발생했습니다.', true);
        },
    });

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            createAlert('이미지 파일을 선택해주세요', '대표 사진은 이미지 파일만 첨부할 수 있습니다.', true);
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            createAlert('이미지 용량이 큽니다', '8MB 이하 이미지를 첨부해주세요.', true);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            setRepresentativeImageBase64(base64);
            setRepresentativeImageUrl(base64);
        };
        reader.readAsDataURL(file);
    };

    const handleSave = () => {
        const payload: CoachPromoProfilePayload = {
            headline,
            shortIntro,
            tags: tagsText
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean),
            isVisible,
            sortOrder,
        };

        if (representativeImageBase64) {
            payload.representativeImageBase64 = representativeImageBase64;
        } else {
            payload.representativeImageUrl = representativeImageUrl;
        }

        updateMutation.mutate(payload);
    };

    return (
        <Col
            className={`${fluid ? 'w-full' : 'w-[1192px]'} bg-white border border-solid border-BLUE_200 rounded-[8px] p-[40px] mb-[16px]`}
            style={{ gap: 28 }}
        >
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Col>
                    <Head3 className="text-LAVEL_4">강사 홍보 프로필</Head3>
                    <Body3Regular className="text-LAVEL_2 mt-[6px]">
                        태블릿 강사 홍보판에 노출되는 {coachName} 강사의 별도 프로필입니다.
                    </Body3Regular>
                </Col>
                <Row style={{ alignItems: 'center', gap: 12 }}>
                    <Body2Regular className="text-LAVEL_4">태블릿 노출</Body2Regular>
                    <NormalToggleButton isActive={isVisible} callBack={() => setIsVisible((prev) => !prev)} />
                </Row>
            </Row>

            <Row style={{ gap: 32 }}>
                <Col style={{ width: 180, gap: 12 }}>
                    <div
                        style={{
                            width: 160,
                            height: 200,
                            borderRadius: 8,
                            border: '1px solid #D7E2F2',
                            backgroundColor: '#F5F8FF',
                            overflow: 'hidden',
                        }}
                    >
                        {displayImageUrl ? (
                            <img
                                src={displayImageUrl}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Row
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: colors.LAVEL_2,
                                    fontSize: 13,
                                }}
                            >
                                대표 사진
                            </Row>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageChange}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                            width: 160,
                            height: 36,
                            borderRadius: 8,
                            border: '1px solid #4A7DFF',
                            color: '#245BDB',
                            backgroundColor: '#F5F8FF',
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        사진 첨부
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setRepresentativeImageUrl('');
                            setRepresentativeImageBase64('');
                        }}
                        style={{
                            width: 160,
                            height: 32,
                            border: 'none',
                            color: colors.LAVEL_2,
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                        }}
                    >
                        사진 제거
                    </button>
                </Col>

                <Col style={{ flex: 1, gap: 20 }}>
                    <Row style={{ gap: 24 }}>
                        <Col style={{ flex: 1, gap: 8 }}>
                            <Body3Regular className="text-LAVEL_4">한 줄 소개</Body3Regular>
                            <input
                                style={inputStyle}
                                value={headline}
                                maxLength={60}
                                placeholder="예: 체형 교정과 근력 향상을 함께 잡는 트레이너"
                                onChange={(event) => setHeadline(event.target.value)}
                            />
                        </Col>
                        <Col style={{ width: 160, gap: 8 }}>
                            <Body3Regular className="text-LAVEL_4">노출 순서</Body3Regular>
                            <input
                                style={inputStyle}
                                type="number"
                                value={sortOrder}
                                onChange={(event) => setSortOrder(Number(event.target.value || 0))}
                            />
                        </Col>
                    </Row>

                    <Col style={{ gap: 8 }}>
                        <Body3Regular className="text-LAVEL_4">전문 분야 태그</Body3Regular>
                        <input
                            style={inputStyle}
                            value={tagsText}
                            placeholder="다이어트, 근력증가, 체형교정"
                            onChange={(event) => setTagsText(event.target.value)}
                        />
                    </Col>

                    <Col style={{ gap: 8 }}>
                        <Body3Regular className="text-LAVEL_4">짧은 소개</Body3Regular>
                        <textarea
                            style={textareaStyle}
                            value={shortIntro}
                            maxLength={260}
                            placeholder="회원이 태블릿에서 읽을 수 있는 짧은 소개를 입력해주세요."
                            onChange={(event) => setShortIntro(event.target.value)}
                        />
                    </Col>
                </Col>
            </Row>

            <Row style={{ justifyContent: 'flex-end' }}>
                <NewColorRoundButton
                    bgColor="primary"
                    textColor="white"
                    text={updateMutation.isLoading ? '저장 중' : '홍보 프로필 저장'}
                    width={150}
                    height={36}
                    onClick={handleSave}
                />
            </Row>
        </Col>
    );
};

export default TrainerPromoProfileComponent;
